import type { MediaTranscriptSegment, SubtitleSidecarFormat } from '@/types/storage'

function parseSubtitleTimestamp(input: string): number | null {
  const match = input.trim().match(/^(?:(\d+):)?(\d{2}):(\d{2})[.,](\d{3})$/)
  if (!match) return null

  const hours = Number.parseInt(match[1] ?? '0', 10)
  const minutes = Number.parseInt(match[2] ?? '0', 10)
  const seconds = Number.parseInt(match[3] ?? '0', 10)
  const milliseconds = Number.parseInt(match[4] ?? '0', 10)
  return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
}

function normalizeCueText(lines: string[]): string {
  return lines.join('\n').trim()
}

function parseSrt(text: string): MediaTranscriptSegment[] {
  const blocks = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n\s*\r?\n/g)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)

  return blocks.flatMap((block) => {
    const lines = block.split(/\r?\n/).map((line) => line.trimEnd())
    if (lines.length === 0) return []

    const timeLineIndex = lines.findIndex((line) => line.includes('-->'))
    if (timeLineIndex === -1) return []

    const [startRaw, endRaw] = lines[timeLineIndex]!.split(/\s+-->\s+/)
    const start = parseSubtitleTimestamp(startRaw ?? '')
    const end = parseSubtitleTimestamp((endRaw ?? '').split(/\s+/)[0] ?? '')
    const cueText = normalizeCueText(lines.slice(timeLineIndex + 1))
    if (start === null || end === null || end <= start || cueText.length === 0) return []

    return [{ start, end, text: cueText }]
  })
}

function parseVtt(text: string): MediaTranscriptSegment[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/)
  const segments: MediaTranscriptSegment[] = []
  let index = 0

  if (lines[0]?.trim().startsWith('WEBVTT')) {
    index = 1
  }

  while (index < lines.length) {
    const line = lines[index]?.trim() ?? ''
    if (!line) {
      index += 1
      continue
    }

    if (line.startsWith('NOTE')) {
      while (index < lines.length && (lines[index]?.trim() ?? '') !== '') {
        index += 1
      }
      continue
    }

    let timeLine = line
    if (!line.includes('-->')) {
      index += 1
      timeLine = lines[index]?.trim() ?? ''
    }

    if (!timeLine.includes('-->')) {
      index += 1
      continue
    }

    const [startRaw, endWithSettings] = timeLine.split(/\s+-->\s+/)
    const start = parseSubtitleTimestamp(startRaw ?? '')
    const end = parseSubtitleTimestamp((endWithSettings ?? '').split(/\s+/)[0] ?? '')
    index += 1

    const cueLines: string[] = []
    while (index < lines.length) {
      const cueLine = lines[index] ?? ''
      if (cueLine.trim() === '') break
      cueLines.push(cueLine)
      index += 1
    }

    const cueText = normalizeCueText(cueLines)
    if (start !== null && end !== null && end > start && cueText.length > 0) {
      segments.push({ start, end, text: cueText })
    }
  }

  return segments
}

export function detectSubtitleSidecarFormat(input: {
  fileName?: string
  mimeType?: string
  rawText?: string
}): SubtitleSidecarFormat | null {
  const fileName = input.fileName?.toLowerCase() ?? ''
  const mimeType = input.mimeType?.toLowerCase() ?? ''
  const rawText = input.rawText?.trimStart() ?? ''

  if (fileName.endsWith('.vtt') || mimeType.includes('vtt') || rawText.startsWith('WEBVTT')) {
    return 'vtt'
  }
  if (fileName.endsWith('.srt') || mimeType.includes('subrip')) {
    return 'srt'
  }
  return null
}

export function parseSubtitleSidecarText(
  text: string,
  format: SubtitleSidecarFormat,
): MediaTranscriptSegment[] {
  return format === 'vtt' ? parseVtt(text) : parseSrt(text)
}